import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { environment } from '@environment';
import { ServiceType } from '@shared/enums/service-type.enum';

import { StorageService } from '@core/services/util/storage.service';
import { LocalStorageService } from '@core/services/util/local-storage.service';
import { UtilityService } from '@core/services/util/utility.service';

@Injectable({
    providedIn: 'root'
})
export class CartApiService {
    private apiUrl: string;
    isDeliveryOrder = true;

    constructor(
        private http: HttpClient,
        private storageService: StorageService,
        private localStorage: LocalStorageService,
        private utilityService: UtilityService
    ) {
        this.apiUrl = environment.c3pServiceUrl;
    }

    create(outletServiceType?: string, tip_amount?: number) {
        const outlet_service_type = this.getServiceType(outletServiceType);
        const body = {
            address_id: this.isDeliveryOrder ? this.storageService.get('address_id') : null,
            cart_items: this._getCartItems(),
            outlet_service_type,
            tip_amount: this.isDeliveryOrder ? tip_amount : 0,
            lat: this.storageService.get('latitude'),
            lon: this.storageService.get('longitude')
        };

        const headers = this.utilityService.getAuthHeaders();
        return this.http.post(`${this.apiUrl}/cart`, body, { headers });
    }

    update(addressId, orderTime, zeroContactDelivery?: boolean) {
        const headers = this.utilityService.getAuthHeaders();

        const body = {
            outlet_id: this.storageService.get('outlet_id'),
            outlet_service_type: this.getServiceType(),
            address_id: this.isDeliveryOrder ? addressId : null,
            order_time: orderTime,
            zero_contact_delivery: zeroContactDelivery,
            lat: this.storageService.get('latitude'),
            lon: this.storageService.get('longitude')
        };

        return this.http.put(this.apiUrl + '/cart', body, { headers });
    }

    getCart() {
        const headers = this.utilityService.getAuthHeaders();

        return this.http.get(this.apiUrl + '/cart', {
            params: {
                outlet_id: this.storageService.get('outlet_id'),
                service_type: this.storageService.get('service_type') || null
            },
            headers: headers
        });
    }

    async applyCoupon(body) {
        return new Promise((resolve, reject) => {
            body = {
                ...body,
                lat: this.storageService.get('latitude'),
                lon: this.storageService.get('longitude')
            };

            const headers = this.utilityService.getAuthHeaders('signed');

            this.http
                .post(this.apiUrl + '/coupons/validate', body, { headers })
                .toPromise()
                .then(data => {
                    resolve(data);
                })
                .catch(error => {
                    reject(error);
                });
        });
    }

    removeCoupon() {
        const headers = this.utilityService.getAuthHeaders('signed');
        const body = {
            lat: this.storageService.get('latitude'),
            lon: this.storageService.get('longitude')
        };
        return this.http.delete(this.apiUrl + '/coupons', { headers, body });
    }

    getProductDetails(requestParams: any) {
        const url = '/catalog/v2/outlets/products';
        const agent_id = this.storageService.get('agent_id');
        const outlet_id = this.storageService.get('outlet_id');
        const service_type = this.storageService.get('service_type');

        const params: any = {
            app_id: environment.brand.id
        };

        if (agent_id) {
            params.agent_id = agent_id;
        }

        if (outlet_id) {
            params.outlet_id = outlet_id;
        }

        if (service_type) {
            params.service_type = service_type;
        }

        return this.http.post(environment.catalogUrl + url, { items: requestParams }, { params });
    }

    addTip(tip_amount) {
        const headers = this.utilityService.getAuthHeaders('signed');
        const body = {
            tip_amount,
            lat: this.storageService.get('latitude'),
            lon: this.storageService.get('longitude')
        };

        return this.http.post(this.apiUrl + '/cart/add_tip', body, { headers });
    }

    removeTip() {
        const headers = this.utilityService.getAuthHeaders('signed');
        const body = {
            lat: this.storageService.get('latitude'),
            lon: this.storageService.get('longitude')
        };
        return this.http.delete(this.apiUrl + '/cart/remove_tip', { headers, body });
    }

    getServiceType(outletServiceType?: string) {
        const addressId = Number(this.storageService.get('address_id'));
        if (outletServiceType === ServiceType.TAKEAWAY || addressId === -1) {
            this.isDeliveryOrder = false;
            return ServiceType.TAKEAWAY;
        }

        if (outletServiceType === ServiceType.DINEIN || addressId === -2) {
            this.isDeliveryOrder = false;
            return ServiceType.DINEIN;
        }

        return ServiceType.DELIVERY;
    }

    private _getCartItems() {
        const cart =
            this.localStorage.get('cart') || Object.create(null, { items: { value: Object.create(null), enumerable: true } });
        const items = Object.values(cart.items).map((item: any) => {
            return {
                id: item.id,
                quantity: item.quantity,
                remark: item.remark
            };
        });
        return JSON.stringify({ items: items });
    }

    getGameInfo(gameData: any) {
        const headers = this.utilityService.getAuthHeaders();

        return this.http.get(this.apiUrl + '/play_win_repeat/games_info', {
            params: {
                ...gameData
            },
            headers
        });
    }

    lockChoice(gameData: any) {
        const headers = this.utilityService.getAuthHeaders('signed');

        return this.http.post(this.apiUrl + '/play_win_repeat/lock_choice', { ...gameData }, { headers });
    }
}
